import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { hu } from "date-fns/locale";
import {
  RefreshCw, Calendar, PhoneCall, Phone, Mail, LogOut, Save,
  Settings as SettingsIcon, Send, Clock, FileText, CheckCircle2, Info,
  Trash2, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

import {
  getBookings, getCallbacks, getTemplates, updateTemplate, sendFollowup,
  apiChangeCredentials, formatApiError, confirmBooking, deleteBooking,
} from "../lib/api";
import { LOGO_URL as LOGO } from "../data/constants";
import { useAuth } from "../context/AuthContext";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return format(new Date(iso), "yyyy.MM.dd HH:mm", { locale: hu }); } catch { return iso; }
};

// Travel-time conflict: another in-person booking on the same day at a DIFFERENT office,
// whose time slot is within ~1.5h (adjacent slot) — not enough time to travel between offices.
const TRAVEL_BUFFER_MIN = 90;
const toMinutes = (t) => {
  const [h, m] = (t || "0:0").split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};
function travelConflict(b, all) {
  if (!b.office || !b.time || !b.date) return false;
  return all.some((o) =>
    o.id !== b.id && o.date === b.date && o.office && o.office !== b.office &&
    Math.abs(toMinutes(o.time) - toMinutes(b.time)) < TRAVEL_BUFFER_MIN
  );
}

function LastContact({ at, template }) {
  if (!at) return <span className="text-muted-foreground/50 text-sm">Nincs</span>;
  return (
    <span className="text-sm flex items-center gap-1.5">
      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
      <span>
        {fmtDate(at)}
        {template && <span className="block text-xs text-muted-foreground">{template}</span>}
      </span>
    </span>
  );
}

// ---- Follow-up email dialog ----
function FollowupDialog({ open, onOpenChange, record, templates, onSent }) {
  const [templateKey, setTemplateKey] = useState("confirmation");
  const [replyTo, setReplyTo] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => { if (open) { setTemplateKey(templates[0]?.key || "confirmation"); } }, [open, templates]);

  if (!record) return null;
  const tpl = templates.find((t) => t.key === templateKey);
  const hasEmail = !!record.data.email;

  const preview = (text) => (text || "")
    .replaceAll("{{name}}", record.data.name || "")
    .replaceAll("{{service}}", record.data.service || "")
    .replaceAll("{{office}}", record.data.office || "")
    .replaceAll("{{mode}}", record.data.mode || "")
    .replaceAll("{{date}}", record.data.date || record.data.preferred_time || "")
    .replaceAll("{{time}}", record.data.time || "");

  const send = async () => {
    setSending(true);
    try {
      const res = await sendFollowup({
        record_type: record.type,
        record_id: record.data.id,
        template_key: templateKey,
        reply_to: replyTo || null,
      });
      toast.success("Email elküldve!", { description: `Címzett: ${record.data.email}` });
      onSent(record.type, record.data.id, res.last_contacted_at, tpl?.name);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="followup-dialog" data-lenis-prevent className="rounded-none border border-foreground bg-background p-0 sm:max-w-2xl max-h-[92vh] overflow-y-auto gap-0">
        <DialogHeader className="text-left space-y-2 p-8 pb-6 border-b border-border">
          <span className="overline text-muted-foreground">Follow-up email</span>
          <DialogTitle className="font-serif-display text-2xl font-medium tracking-tight">
            Email küldése — {record.data.name}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {hasEmail ? `Címzett: ${record.data.email}` : "Ehhez az ügyfélhez nincs email cím rögzítve."}
          </DialogDescription>
        </DialogHeader>

        <div className="p-8 space-y-6">
          {!hasEmail ? (
            <div className="border border-dashed border-border p-6 text-sm text-muted-foreground flex items-center gap-3">
              <Info className="h-5 w-5 shrink-0" />
              Email küldéshez az ügyfélnek meg kellett volna adnia az email címét a foglaláskor.
            </div>
          ) : (
            <>
              <div>
                <Label className="overline block mb-2 text-muted-foreground">Sablon</Label>
                <Select value={templateKey} onValueChange={setTemplateKey}>
                  <SelectTrigger data-testid="followup-template" className="rounded-none border border-border bg-white h-11 px-3 focus:ring-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {templates.map((t) => (
                      <SelectItem key={t.key} value={t.key} className="rounded-none">{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="overline block mb-2 text-muted-foreground">Válasz-cím (reply-to, opcionális)</Label>
                <Input data-testid="followup-replyto" className="rounded-none border border-border bg-white h-11 focus-visible:ring-0 focus-visible:border-foreground"
                  value={replyTo} onChange={(e) => setReplyTo(e.target.value)} placeholder="pl. adam@sajatdomain.hu" />
              </div>

              <div>
                <Label className="overline block mb-2 text-muted-foreground">Előnézet</Label>
                <div className="border border-border bg-secondary/40 p-5">
                  <p className="text-sm font-medium mb-3">{preview(tpl?.subject)}</p>
                  <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">{preview(tpl?.body)}</p>
                </div>
              </div>

              <Button data-testid="followup-send" onClick={send} disabled={sending}
                className="w-full rounded-none bg-foreground text-background hover:bg-[#2A2A2A] h-12 group">
                <Send className="mr-2 h-4 w-4" />
                {sending ? "Küldés…" : "Email elküldése"}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---- Templates editor ----
function TemplatesEditor({ templates, reload }) {
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    const d = {};
    templates.forEach((t) => { d[t.key] = { subject: t.subject, body: t.body }; });
    setDrafts(d);
  }, [templates]);

  const save = async (t) => {
    setSavingKey(t.key);
    try {
      await updateTemplate(t.key, { name: t.name, subject: drafts[t.key].subject, body: drafts[t.key].body });
      toast.success(`"${t.name}" sablon mentve`);
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingKey(null);
    }
  };

  const setField = (key, field) => (v) =>
    setDrafts((d) => ({ ...d, [key]: { ...d[key], [field]: v } }));

  return (
    <div className="space-y-8" data-testid="templates-editor">
      <div className="border border-border bg-secondary/40 p-5 text-sm text-muted-foreground">
        Használható helyettesítők: <code className="font-mono-label">{"{{name}}"}</code>,{" "}
        <code className="font-mono-label">{"{{service}}"}</code>,{" "}
        <code className="font-mono-label">{"{{office}}"}</code>,{" "}
        <code className="font-mono-label">{"{{date}}"}</code>,{" "}
        <code className="font-mono-label">{"{{time}}"}</code>
      </div>
      {templates.map((t) => (
        <div key={t.key} data-testid={`template-${t.key}`} className="border border-border p-6">
          <div className="flex items-center gap-3 mb-5">
            <FileText className="h-4 w-4" />
            <h3 className="font-serif-display text-xl">{t.name}</h3>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="overline block mb-2 text-muted-foreground">Tárgy</Label>
              <Input data-testid={`template-${t.key}-subject`} className="rounded-none border border-border bg-white h-11 focus-visible:ring-0 focus-visible:border-foreground"
                value={drafts[t.key]?.subject || ""} onChange={(e) => setField(t.key, "subject")(e.target.value)} />
            </div>
            <div>
              <Label className="overline block mb-2 text-muted-foreground">Szöveg</Label>
              <Textarea data-testid={`template-${t.key}-body`} className="rounded-none border border-border bg-white focus-visible:ring-0 focus-visible:border-foreground min-h-[180px] font-mono text-sm"
                value={drafts[t.key]?.body || ""} onChange={(e) => setField(t.key, "body")(e.target.value)} />
            </div>
            <Button data-testid={`template-${t.key}-save`} onClick={() => save(t)} disabled={savingKey === t.key}
              className="rounded-none bg-foreground text-background hover:bg-[#2A2A2A] h-11">
              <Save className="mr-2 h-4 w-4" />
              {savingKey === t.key ? "Mentés…" : "Sablon mentése"}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---- Settings (change credentials) ----
function SettingsPanel() {
  const { setUser } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_username: "", new_password: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.current_password) { toast.error("Adja meg a jelenlegi jelszót."); return; }
    setSaving(true);
    try {
      const res = await apiChangeCredentials(form);
      localStorage.setItem("pm_admin_token", res.access_token);
      setUser({ username: res.username });
      toast.success("Belépési adatok frissítve");
      setForm({ current_password: "", new_username: "", new_password: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "rounded-none border border-border bg-white h-11 focus-visible:ring-0 focus-visible:border-foreground";

  return (
    <div className="max-w-md" data-testid="settings-panel">
      <div className="border border-border p-6">
        <h3 className="font-serif-display text-xl mb-1">Belépési adatok módosítása</h3>
        <p className="text-sm text-muted-foreground mb-6">Módosítsa felhasználónevét és/vagy jelszavát.</p>
        <form onSubmit={save} className="space-y-5">
          <div>
            <Label className="overline block mb-2 text-muted-foreground">Jelenlegi jelszó *</Label>
            <Input data-testid="settings-current" type="password" className={inputCls}
              value={form.current_password} onChange={set("current_password")} />
          </div>
          <div>
            <Label className="overline block mb-2 text-muted-foreground">Új felhasználónév</Label>
            <Input data-testid="settings-username" className={inputCls}
              value={form.new_username} onChange={set("new_username")} placeholder="hagyja üresen, ha nem változik" />
          </div>
          <div>
            <Label className="overline block mb-2 text-muted-foreground">Új jelszó</Label>
            <Input data-testid="settings-password" type="password" className={inputCls}
              value={form.new_password} onChange={set("new_password")} placeholder="min. 6 karakter" />
          </div>
          <Button type="submit" disabled={saving} data-testid="settings-save"
            className="rounded-none bg-foreground text-background hover:bg-[#2A2A2A] h-11">
            <Save className="mr-2 h-4 w-4" />
            {saving ? "Mentés…" : "Mentés"}
          </Button>
        </form>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [callbacks, setCallbacks] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followup, setFollowup] = useState({ open: false, record: null });

  const load = async () => {
    setLoading(true);
    try {
      const [b, c, t] = await Promise.all([getBookings(), getCallbacks(), getTemplates()]);
      setBookings(b); setCallbacks(c); setTemplates(t);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onSent = (type, id, at, tplName) => {
    const patch = (arr) => arr.map((r) => r.id === id ? { ...r, last_contacted_at: at, last_template: tplName } : r);
    if (type === "booking") setBookings(patch); else setCallbacks(patch);
  };

  const [confirmingId, setConfirmingId] = useState(null);
  const doConfirm = async (b) => {
    if (!b.email) { toast.error("Ehhez a foglaláshoz nincs email cím — nem küldhető visszaigazolás."); return; }
    setConfirmingId(b.id);
    try {
      const res = await confirmBooking(b.id);
      toast.success("Foglalás visszaigazolva!", { description: `Email elküldve: ${b.email}` });
      setBookings((arr) => arr.map((r) => r.id === b.id
        ? { ...r, status: "confirmed", last_contacted_at: res.last_contacted_at, last_template: res.last_template } : r));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setConfirmingId(null);
    }
  };

  const [deletingId, setDeletingId] = useState(null);
  const doDelete = async (b) => {
    if (!window.confirm(`Biztosan elutasítja / törli ${b.name} foglalását? (${b.date} ${b.time})\nAz időpont ismét szabaddá válik.`)) return;
    setDeletingId(b.id);
    try {
      await deleteBooking(b.id);
      toast.success("Foglalás törölve — az időpont felszabadult.");
      setBookings((arr) => arr.filter((r) => r.id !== b.id));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeletingId(null);
    }
  };

  const openFollowup = (type, data) => setFollowup({ open: true, record: { type, data } });

  const doLogout = () => { logout(); navigate("/admin/login", { replace: true }); };

  return (
    <div data-testid="admin-page" className="pt-28 min-h-screen">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 pb-24">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 border-b border-border pb-8">
          <div className="flex items-center gap-4">
            <img src={LOGO} alt="A/Z" className="h-12 w-12 object-contain" />
            <div>
              <span className="overline text-muted-foreground">Admin — {user?.username}</span>
              <h1 className="font-serif-display text-3xl lg:text-4xl tracking-tight mt-1">Ügyfélkezelő</h1>
            </div>
          </div>
          <div className="flex gap-3">
            <Button data-testid="admin-refresh-btn" onClick={load} variant="outline"
              className="rounded-none border-foreground h-11 group">
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : "group-hover:rotate-90 transition-transform"}`} />
              Frissítés
            </Button>
            <Button data-testid="admin-logout-btn" onClick={doLogout} variant="outline"
              className="rounded-none border-foreground h-11">
              <LogOut className="mr-2 h-4 w-4" /> Kilépés
            </Button>
          </div>
        </div>

        <Tabs defaultValue="bookings" className="mt-10">
          <TabsList className="rounded-none bg-secondary p-1 h-auto flex-wrap">
            {[
              { v: "bookings", icon: Calendar, label: `Foglalások (${bookings.length})`, tid: "tab-bookings" },
              { v: "callbacks", icon: PhoneCall, label: `Visszahívások (${callbacks.length})`, tid: "tab-callbacks" },
              { v: "templates", icon: FileText, label: "Email sablonok", tid: "tab-templates" },
              { v: "settings", icon: SettingsIcon, label: "Beállítások", tid: "tab-settings" },
            ].map((t) => (
              <TabsTrigger key={t.v} value={t.v} data-testid={t.tid}
                className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background px-5 py-2.5">
                <t.icon className="mr-2 h-4 w-4" /> {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Bookings */}
          <TabsContent value="bookings" className="mt-8">
            <div className="border border-border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-secondary/60">
                    <TableHead className="overline">Ügyfél</TableHead>
                    <TableHead className="overline">Szolgáltatás</TableHead>
                    <TableHead className="overline">Iroda</TableHead>
                    <TableHead className="overline">Időpont</TableHead>
                    <TableHead className="overline">Státusz</TableHead>
                    <TableHead className="overline">Utolsó kommunikáció</TableHead>
                    <TableHead className="overline text-right">Műveletek</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bookings.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-12">Nincs foglalás.</TableCell></TableRow>
                  ) : bookings.map((b) => {
                    const conflict = travelConflict(b, bookings);
                    return (
                    <TableRow key={b.id} data-testid={`booking-row-${b.id}`} className={conflict ? "bg-amber-50" : ""}>
                      <TableCell>
                        <div className="font-medium">{b.name}</div>
                        <div className="text-xs text-muted-foreground">{b.phone}</div>
                        {b.email && <div className="text-xs text-muted-foreground">{b.email}</div>}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="rounded-none">{b.service}</Badge>
                        {b.mode && <span className="block text-xs text-muted-foreground mt-1.5">{b.mode}</span>}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm max-w-[200px]">{b.office || <span className="italic">{b.mode}</span>}</TableCell>
                      <TableCell className="text-sm">{b.date}<span className="block text-muted-foreground">{b.time}</span></TableCell>
                      <TableCell>
                        {b.status === "confirmed" ? (
                          <Badge className="rounded-none bg-emerald-600 hover:bg-emerald-600 text-white">Visszaigazolva</Badge>
                        ) : (
                          <Badge variant="outline" className="rounded-none">Új</Badge>
                        )}
                        {conflict && (
                          <span data-testid={`booking-conflict-${b.id}`}
                            className="mt-2 flex items-center gap-1.5 text-xs text-amber-700"
                            title="Egy másik, közeli időpontú foglalás van másik irodában — nem biztos, hogy elég az utazási idő.">
                            <AlertTriangle className="h-3.5 w-3.5" /> Utazási ütközés
                          </span>
                        )}
                      </TableCell>
                      <TableCell><LastContact at={b.last_contacted_at} template={b.last_template} /></TableCell>
                      <TableCell>
                        <div className="flex gap-2 justify-end">
                          <a href={`tel:${b.phone}`} data-testid={`booking-call-${b.id}`}
                            className="inline-flex items-center justify-center h-9 w-9 border border-border hover:border-foreground transition-colors" title="Hívás">
                            <Phone className="h-4 w-4" />
                          </a>
                          <button data-testid={`booking-email-${b.id}`} onClick={() => openFollowup("booking", b)}
                            disabled={!b.email}
                            className="inline-flex items-center justify-center h-9 w-9 border border-border hover:border-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed" title="Email küldése">
                            <Mail className="h-4 w-4" />
                          </button>
                          <button data-testid={`booking-confirm-${b.id}`} onClick={() => doConfirm(b)}
                            disabled={!b.email || b.status === "confirmed" || confirmingId === b.id}
                            className="inline-flex items-center gap-1.5 h-9 px-3 border border-foreground bg-foreground text-background text-xs hover:bg-[#2A2A2A] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            title={b.status === "confirmed" ? "Már visszaigazolva" : "Visszaigazolás emailben"}>
                            <CheckCircle2 className="h-4 w-4" />
                            {b.status === "confirmed" ? "Visszaigazolva" : (confirmingId === b.id ? "Küldés…" : "Visszaigazolás")}
                          </button>
                          <button data-testid={`booking-delete-${b.id}`} onClick={() => doDelete(b)}
                            disabled={deletingId === b.id}
                            className="inline-flex items-center justify-center h-9 w-9 border border-border text-destructive hover:border-destructive transition-colors disabled:opacity-40" title="Elutasítás / törlés">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Callbacks */}
          <TabsContent value="callbacks" className="mt-8">
            <div className="border border-border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-secondary/60">
                    <TableHead className="overline">Ügyfél</TableHead>
                    <TableHead className="overline">Mikor hívjuk</TableHead>
                    <TableHead className="overline">Téma</TableHead>
                    <TableHead className="overline">Megjegyzés</TableHead>
                    <TableHead className="overline">Utolsó kommunikáció</TableHead>
                    <TableHead className="overline text-right">Műveletek</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {callbacks.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-12">Nincs visszahívási kérés.</TableCell></TableRow>
                  ) : callbacks.map((c) => (
                    <TableRow key={c.id} data-testid={`callback-row-${c.id}`}>
                      <TableCell>
                        <div className="font-medium">{c.name}</div>
                        <div className="text-xs text-muted-foreground">{c.phone}</div>
                        {c.email && <div className="text-xs text-muted-foreground">{c.email}</div>}
                      </TableCell>
                      <TableCell className="text-sm">{c.preferred_time || "—"}</TableCell>
                      <TableCell>{c.service ? <Badge variant="outline" className="rounded-none">{c.service}</Badge> : "—"}</TableCell>
                      <TableCell className="text-muted-foreground text-sm max-w-[220px]">{c.message || "—"}</TableCell>
                      <TableCell><LastContact at={c.last_contacted_at} template={c.last_template} /></TableCell>
                      <TableCell>
                        <div className="flex gap-2 justify-end">
                          <a href={`tel:${c.phone}`} data-testid={`callback-call-${c.id}`}
                            className="inline-flex items-center justify-center h-9 w-9 border border-border hover:border-foreground transition-colors" title="Hívás">
                            <Phone className="h-4 w-4" />
                          </a>
                          <button data-testid={`callback-email-${c.id}`} onClick={() => openFollowup("callback", c)}
                            disabled={!c.email}
                            className="inline-flex items-center justify-center h-9 w-9 border border-border hover:border-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed" title="Email küldése">
                            <Mail className="h-4 w-4" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <p className="text-xs text-muted-foreground mt-4 flex items-center gap-2">
              <Info className="h-3.5 w-3.5" /> A visszahívás-kérők email címet is megadnak, így follow-up email küldhető nekik.
            </p>
          </TabsContent>

          <TabsContent value="templates" className="mt-8">
            <TemplatesEditor templates={templates} reload={load} />
          </TabsContent>

          <TabsContent value="settings" className="mt-8">
            <SettingsPanel />
          </TabsContent>
        </Tabs>
      </div>

      <FollowupDialog
        open={followup.open}
        onOpenChange={(o) => setFollowup((f) => ({ ...f, open: o }))}
        record={followup.record}
        templates={templates}
        onSent={onSent}
      />
    </div>
  );
}
